import { useState, useRef, useCallback } from "react";
import { Bell, CheckCheck, ExternalLink, TrendingDown, CalendarClock, Mail, Inbox } from "lucide-react";
import { useNavigate } from "react-router";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { formatRelativeDate } from "@/lib/utils";
import { cn } from "@/lib/utils";

// ─── Notification type helpers ───────────────────────

type NotificationFilter = "all" | "price_alert" | "reminder" | "rsvp";

const FILTER_TABS: { value: NotificationFilter; label: string; icon: React.ReactNode }[] = [
  { value: "all", label: "All", icon: <Inbox className="h-3 w-3" /> },
  { value: "price_alert", label: "Prices", icon: <TrendingDown className="h-3 w-3" /> },
  { value: "reminder", label: "Reminders", icon: <CalendarClock className="h-3 w-3" /> },
  { value: "rsvp", label: "RSVPs", icon: <Mail className="h-3 w-3" /> },
];

function matchesFilter(notification: Notification, filter: NotificationFilter): boolean {
  if (filter === "all") return true;
  if (filter === "reminder") return notification.type === "reminder" || notification.type === "periodic_reminder";
  return notification.type === filter;
}

function getNotificationLink(notification: Notification): { url: string; external: boolean } | null {
  const data = notification.data;
  if (!data) return null;

  switch (notification.type) {
    case "price_alert":
      // priceCheckWorker stores affiliateUrl; priceDropWorker stores amazonUrl
      if (data.affiliateUrl && typeof data.affiliateUrl === "string") return { url: data.affiliateUrl, external: true };
      if (data.amazonUrl && typeof data.amazonUrl === "string") return { url: data.amazonUrl, external: true };
      return null;
    case "reminder":
    case "periodic_reminder":
      if (data.contactId && typeof data.contactId === "string") return { url: `/contacts/${data.contactId}`, external: false };
      return null;
    case "rsvp":
      if (data.cardId && typeof data.cardId === "string") return { url: `/cards/${data.cardId}`, external: false };
      return null;
    default:
      return null;
  }
}

function getPriceDropPercent(notification: Notification): number | null {
  if (notification.type !== "price_alert") return null;
  const data = notification.data;
  if (!data) return null;

  // priceDropWorker stores percentDrop as a decimal (0.15 = 15%)
  // priceCheckWorker stores dropPercent as a number (15 = 15%)
  if (typeof data.percentDrop === "number") {
    return data.percentDrop < 1 ? Math.round(data.percentDrop * 100) : Math.round(data.percentDrop);
  }
  if (typeof data.dropPercent === "number") {
    return Math.round(data.dropPercent);
  }
  return null;
}

// ─── Swipe-to-dismiss hook ───────────────────────

function useSwipeToDismiss(onDismiss: () => void) {
  const startX = useRef<number | null>(null);
  const currentX = useRef<number>(0);
  const elementRef = useRef<HTMLButtonElement>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    currentX.current = 0;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (startX.current === null) return;
    const diff = e.touches[0].clientX - startX.current;
    // Only allow right swipe (dismiss direction)
    if (diff > 0 && elementRef.current) {
      currentX.current = diff;
      elementRef.current.style.transform = `translateX(${Math.min(diff, 150)}px)`;
      elementRef.current.style.opacity = `${Math.max(1 - diff / 200, 0.3)}`;
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (startX.current === null) return;
    if (currentX.current > 100) {
      // Threshold met — dismiss
      if (elementRef.current) {
        elementRef.current.style.transform = "translateX(300px)";
        elementRef.current.style.opacity = "0";
        setTimeout(onDismiss, 200);
      }
    } else {
      // Reset position
      if (elementRef.current) {
        elementRef.current.style.transition = "transform 0.2s, opacity 0.2s";
        elementRef.current.style.transform = "translateX(0)";
        elementRef.current.style.opacity = "1";
        setTimeout(() => {
          if (elementRef.current) {
            elementRef.current.style.transition = "";
          }
        }, 200);
      }
    }
    startX.current = null;
    currentX.current = 0;
  }, [onDismiss]);

  return { elementRef, onTouchStart, onTouchMove, onTouchEnd };
}

// ─── NotificationItem ───────────────────────

function NotificationItem({
  notification,
  onRead,
  onNavigate,
}: {
  notification: Notification;
  onRead: (id: string) => void;
  onNavigate: (url: string, external: boolean) => void;
}) {
  const link = getNotificationLink(notification);
  const dropPercent = getPriceDropPercent(notification);

  const handleDismiss = useCallback(() => {
    if (!notification.read) onRead(notification.id);
  }, [notification.id, notification.read, onRead]);

  const { elementRef, onTouchStart, onTouchMove, onTouchEnd } = useSwipeToDismiss(handleDismiss);

  const handleClick = () => {
    if (!notification.read) onRead(notification.id);
    if (link) onNavigate(link.url, link.external);
  };

  return (
    <button
      ref={elementRef}
      onClick={handleClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className={cn(
        "w-full text-left p-3 border-b border-white/5 hover:bg-white/5 transition-colors",
        !notification.read && "bg-primary/5",
        link && "cursor-pointer"
      )}
    >
      <div className="flex items-start gap-2">
        {!notification.read && (
          <div className="mt-1.5 h-2 w-2 rounded-full bg-gift-pink-500 shrink-0" />
        )}
        <div className={cn("flex-1 min-w-0", notification.read && "ml-4")}>
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-medium truncate">{notification.title}</p>
            {dropPercent !== null && (
              <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
                <TrendingDown className="h-2.5 w-2.5" />
                -{dropPercent}%
              </span>
            )}
            {link?.external && (
              <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {notification.body}
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            {formatRelativeDate(notification.createdAt)}
          </p>
        </div>
      </div>
    </button>
  );
}

// ─── NotificationPanel ───────────────────────

export function NotificationPanel() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationFilter>("all");
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    loadMore,
    markAsRead,
    markAllRead,
  } = useNotifications();

  const handleNavigate = (url: string, external: boolean) => {
    if (external) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      navigate(url);
    }
  };

  const filteredNotifications = notifications.filter((n) => matchesFilter(n, filter));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-gift-pink-500 text-[10px] font-bold text-white flex items-center justify-center">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={markAllRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 p-2 border-b border-white/5" role="tablist" aria-label="Notification filters">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              role="tab"
              aria-selected={filter === tab.value}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors",
                filter === tab.value
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        <ScrollArea className="max-h-80">
          {filteredNotifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {isLoading ? "Loading..." : filter === "all" ? "No notifications yet" : "No notifications in this category"}
            </div>
          ) : (
            <>
              {filteredNotifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onRead={markAsRead}
                  onNavigate={handleNavigate}
                />
              ))}
              {hasMore && filter === "all" && (
                <button
                  onClick={loadMore}
                  className="w-full p-2 text-xs text-primary hover:underline"
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "Load more"}
                </button>
              )}
            </>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
